import Link from 'next/link';

import { Pill } from 'lucide-react';

import { cn } from '@kit/ui/utils';

function LekolizjaLogoImage({
  className,
  showName = false,
  size = 'default',
  hideTld = false,
}: {
  className?: string;
  showName?: boolean;
  size?: 'default' | 'small';
  /** Omit the ".pl" suffix (English demo) */
  hideTld?: boolean;
}) {
  const isSmall = size === 'small';

  return (
    <div
      className={cn(
        'group flex items-center',
        isSmall ? 'gap-1.5' : 'gap-2',
        className,
      )}
    >
      {/* Pills container with padding for shadow visibility */}
      <div className="flex items-center pr-0.5 pb-0.5">
        <div
          className={cn(
            'z-10 rounded-full border-zinc-900 bg-white transition-all duration-300 ease-in-out group-hover:z-20 group-hover:rotate-[15deg] dark:border-zinc-100 dark:bg-zinc-800',
            isSmall
              ? 'border p-0.5 shadow-[1px_1px_0_0_#18181b] group-hover:translate-x-1 dark:shadow-[1px_1px_0_0_#f4f4f5]'
              : 'border-2 p-1.5 shadow-[1px_1px_0_0_#18181b] group-hover:translate-x-2 dark:shadow-[1px_1px_0_0_#f4f4f5]',
          )}
        >
          <Pill
            className={cn(
              'rotate-[-15deg] text-zinc-900 transition-transform duration-300 group-hover:rotate-[15deg] dark:text-zinc-100',
              isSmall ? 'h-2.5 w-2.5' : 'h-4 w-4',
            )}
          />
        </div>
        <div
          className={cn(
            'rounded-full border-zinc-900 bg-white transition-all duration-300 ease-in-out group-hover:rotate-[15deg] dark:border-zinc-100 dark:bg-zinc-800',
            isSmall
              ? '-ml-1 border p-0.5 shadow-[1px_1px_0_0_#18181b] group-hover:-translate-x-1 dark:shadow-[1px_1px_0_0_#f4f4f5]'
              : '-ml-2 border-2 p-1.5 shadow-[1px_1px_0_0_#18181b] group-hover:-translate-x-2 dark:shadow-[1px_1px_0_0_#f4f4f5]',
          )}
        >
          <Pill
            className={cn(
              'rotate-[15deg] text-zinc-900 transition-transform duration-300 group-hover:rotate-[15deg] dark:text-zinc-100',
              isSmall ? 'h-2.5 w-2.5' : 'h-4 w-4',
            )}
          />
        </div>
      </div>
      {showName && (
        <span
          className={cn(
            'font-heading font-semibold',
            isSmall ? 'text-xs' : 'text-lg',
          )}
        >
          <span className="text-zinc-400 transition-colors duration-300 group-hover:text-zinc-900 dark:text-zinc-500 dark:group-hover:text-white">
            LE
          </span>
          <span className="text-zinc-900 dark:text-white">K</span>
          <span className="text-zinc-900 transition-colors duration-300 group-hover:text-zinc-200 dark:text-white dark:group-hover:text-zinc-700">
            olizja
          </span>
          {!hideTld && (
            <span className="text-zinc-400 transition-colors duration-300 group-hover:text-zinc-200 dark:text-zinc-500 dark:group-hover:text-zinc-700">
              .pl
            </span>
          )}
        </span>
      )}
    </div>
  );
}

export function AppLogo({
  href,
  label,
  className,
  showName = true,
  size = 'default',
  onClick,
  hideTld = false,
}: {
  href?: string | null;
  className?: string;
  label?: string;
  showName?: boolean;
  size?: 'default' | 'small';
  onClick?: (e: React.MouseEvent) => void;
  /** Omit the ".pl" suffix (English demo) */
  hideTld?: boolean;
}) {
  if (href === null) {
    return (
      <LekolizjaLogoImage
        className={className}
        showName={showName}
        size={size}
        hideTld={hideTld}
      />
    );
  }

  return (
    <Link
      aria-label={label ?? 'LEKolizja.pl'}
      href={href ?? '/'}
      prefetch={true}
      onClick={onClick}
    >
      <LekolizjaLogoImage
        className={className}
        showName={showName}
        size={size}
        hideTld={hideTld}
      />
    </Link>
  );
}

/**
 * @deprecated Use AppLogo instead - kept for backwards compatibility
 */
export function PillsLogo({
  href,
  label,
  className,
  showName = false,
  size = 'default',
  onClick,
}: {
  href?: string | null;
  className?: string;
  label?: string;
  showName?: boolean;
  size?: 'default' | 'small';
  onClick?: (e: React.MouseEvent) => void;
}) {
  return (
    <AppLogo
      href={href}
      label={label}
      className={className}
      showName={showName}
      size={size}
      onClick={onClick}
    />
  );
}
